import { map } from 'rxjs/operators';
import { Inject, Injectable } from '@angular/core';
import { MessageService } from '../../core/service/message.service';
import { Observable } from 'rxjs';
import { QueryService } from './query.service';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { CONSTANTS } from '../../config/constants.module';

export class SearchListItem {
  key: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class TypeaheadService {
  public searchLoading: boolean;
  public searchNoResults: boolean;
  public selectedSearchItem: SearchListItem;

  constructor(
    private _messageService: MessageService,
    private _queryService: QueryService,
    @Inject(CONSTANTS) private _CONST: any
  ) {}

  public onSearchLoading(e: boolean): void {
    this.searchLoading = e;
    if (this.searchLoading) {
      this.selectedSearchItem = undefined;
    }
  }

  /**
   * @name onSearchNoResults
   * @returns false/true
   * @description Event-Listener fired on search text in the typeahead list
   * @param e
   */
  public onSearchNoResults(e: boolean): void {
    this.searchNoResults = e;
  }

  /**
   * @name onSearchSelect
   * @returns selected item
   * @description Event-Listener fired on selected item in the typeahead list
   * @param e
   */
  public onSearchSelect(e: TypeaheadMatch): void {
    this.selectedSearchItem = e.item;
  }

  /**
   * @name querySearchSource
   * @param searchUrl
   * @param searchField
   * @param idField
   * @param token
   * @param options
   * @returns {Observable<R>}
   * @description Queries the related table based on given token
   */
  public querySearchSource(
    searchUrl: string,
    searchField: string,
    idField: string,
    token: string,
    options?: {
      where?: string;
      distinct?: boolean;
      noLimit?: boolean;
      outFields?: string[];
      orderBy?: string[];
      customWhere?: boolean;
      nameFormatter?: (args: object, fields: string[]) => string;
      sort?: (searchItems: SearchListItem[]) => SearchListItem[];
    }
  ): Observable<SearchListItem> {
    const params = { ...options };
    // define query
    let where: string = `LOWER(CAST(${searchField} AS VARCHAR(256))) like '%${token.toLocaleLowerCase()}%'`;

    if (params && params.where) {
      where = !params.customWhere ? where + ' AND ' + params.where : params.where;
    }

    const outFields = params.outFields || [idField, searchField];
    const query: __esri.Query = this._queryService.buildQuery(false, where, outFields);

    if (!params.noLimit) query.num = 20;
    if (params.orderBy) query.orderByFields = params.orderBy;
    if (params.distinct) query.returnDistinctValues = true;

    const errorCode = this._CONST.LAYER_ERROR.GET_FEATURES_FROM_LAYER_FAILED;

    // execute query
    return this._queryService.execute(searchUrl, query, errorCode).pipe(
      map(result => {
        if (!result || !result.features || result.features.length === 0) return;
        const searchItems = result.features.map(m => {
          const item: SearchListItem = {
            key: m.attributes[idField],
            name: params.nameFormatter
              ? params.nameFormatter(m.attributes, params.outFields || [searchField])
              : m.attributes[searchField],
          };
          return item;
        });
        return params.sort ? params.sort(searchItems) : searchItems;
      })
    );
  }
}
